// src/pages/trading-portal/AIAnalysisEngine/SentimentAlternativeDataPage.js
// Sentiment & Alternative Data - tabbed interface for news, social, events, options flow, macro

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Grid,
    Chip,
    CircularProgress,
    Tabs,
    Tab,
    Skeleton,
    Card,
    CardContent,
    Tooltip,
    LinearProgress,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Newspaper as NewspaperIcon,
    Forum as ForumIcon,
    Event as EventIcon,
    Waves as WavesIcon,
    AccountBalance as AccountBalanceIcon,
    PlayArrow as PlayArrowIcon,
    OpenInNew as OpenInNewIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    SentimentScoreLabels,
    SentimentScoreColors,
    EventImpactLevelLabels,
    EventImpactLevelColors,
    FlowSignalTypeLabels,
    FlowSignalTypeColors,
    MacroIndicatorSignalLabels,
    MacroIndicatorSignalColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const SentimentAlternativeDataPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);

    // Tab
    const [activeTab, setActiveTab] = useState(0);

    // Global state
    const [error, setError] = useState(null);

    // ========== Tab 0: News Sentiment ==========
    const [newsItems, setNewsItems] = useState([]);
    const [newsLoading, setNewsLoading] = useState(true);
    const [newsPage, setNewsPage] = useState(0);
    const [newsRowsPerPage, setNewsRowsPerPage] = useState(10);
    const [newsTotalCount, setNewsTotalCount] = useState(0);
    const [analyzingNews, setAnalyzingNews] = useState(false);
    const [fetchingLiveNews, setFetchingLiveNews] = useState(false);
    const [liveNewsFeedback, setLiveNewsFeedback] = useState(null);
    const [newsSymbolFilter, setNewsSymbolFilter] = useState('');
    const [watchlistSymbols, setWatchlistSymbols] = useState([]);

    // ========== Tab 1: Social Media ==========
    const [socialData, setSocialData] = useState([]);
    const [socialLoading, setSocialLoading] = useState(false);
    const [socialPage, setSocialPage] = useState(0);
    const [socialRowsPerPage, setSocialRowsPerPage] = useState(10);
    const [socialTotalCount, setSocialTotalCount] = useState(0);

    // ========== Tab 2: Events ==========
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [eventsPage, setEventsPage] = useState(0);
    const [eventsRowsPerPage, setEventsRowsPerPage] = useState(10);
    const [eventsTotalCount, setEventsTotalCount] = useState(0);

    // ========== Tab 3: Options Flow ==========
    const [flowData, setFlowData] = useState([]);
    const [flowLoading, setFlowLoading] = useState(false);
    const [flowPage, setFlowPage] = useState(0);
    const [flowRowsPerPage, setFlowRowsPerPage] = useState(10);
    const [flowTotalCount, setFlowTotalCount] = useState(0);

    // ========== Tab 4: Macro Indicators ==========
    const [macroData, setMacroData] = useState([]);
    const [macroLoading, setMacroLoading] = useState(false);
    const [macroPage, setMacroPage] = useState(0);
    const [macroRowsPerPage, setMacroRowsPerPage] = useState(10);
    const [macroTotalCount, setMacroTotalCount] = useState(0);

    // ========== Permissions ==========
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-aae-sentiment', 'view');
            const add = await hasPermission('tr-aae-sentiment', 'add');
            setCanView(view);
            setCanAdd(add);
        };
        checkPermissions();
    }, []);

    // ========== Fetch Watchlist Symbols ==========
    const fetchWatchlistSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setWatchlistSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch watchlist symbols:', err);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchWatchlistSymbols();
    }, [canView, fetchWatchlistSymbols]);

    // ========== Tab 0: News Sentiment ==========
    const fetchNews = useCallback(async () => {
        if (!canView) return;
        setNewsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: newsPage + 1, per_page: newsRowsPerPage });
            if (newsSymbolFilter) params.append('symbol_id', newsSymbolFilter);
            const response = await axiosInstance.get(`/api/sentiment-data/news?${params.toString()}`);
            if (response.data.success) {
                setNewsItems(response.data.data || []);
                setNewsTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch news sentiment');
        } finally {
            setNewsLoading(false);
        }
    }, [canView, newsPage, newsRowsPerPage, newsSymbolFilter]);

    useEffect(() => {
        if (canView && activeTab === 0) fetchNews();
    }, [fetchNews, canView, activeTab]);

    const handleAnalyzeNews = async () => {
        setAnalyzingNews(true);
        try {
            await axiosInstance.post('/api/sentiment-data/news/analyze');
            fetchNews();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to analyze news');
        } finally {
            setAnalyzingNews(false);
        }
    };

    const handleFetchLiveNews = async () => {
        setFetchingLiveNews(true);
        setLiveNewsFeedback(null);
        try {
            const response = await axiosInstance.post('/api/sentiment-data/news/fetch-live');
            if (response.data.success) {
                const { fetched, stored } = response.data.data || {};
                setLiveNewsFeedback({ type: 'success', message: `Fetched ${fetched} articles, stored ${stored} new records` });
                fetchNews();
            } else {
                setLiveNewsFeedback({ type: 'error', message: response.data.message || 'Failed to fetch live news' });
            }
        } catch (err) {
            setLiveNewsFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to fetch live news' });
        } finally {
            setFetchingLiveNews(false);
        }
    };

    // ========== Tab 1: Social Media ==========
    const fetchSocial = useCallback(async () => {
        if (!canView) return;
        setSocialLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: socialPage + 1, per_page: socialRowsPerPage });
            const response = await axiosInstance.get(`/api/sentiment-data/social?${params.toString()}`);
            if (response.data.success) {
                setSocialData(response.data.data || []);
                setSocialTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch social data');
        } finally {
            setSocialLoading(false);
        }
    }, [canView, socialPage, socialRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 1) fetchSocial();
    }, [fetchSocial, canView, activeTab]);

    // ========== Tab 2: Events ==========
    const fetchEvents = useCallback(async () => {
        if (!canView) return;
        setEventsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: eventsPage + 1, per_page: eventsRowsPerPage });
            const response = await axiosInstance.get(`/api/sentiment-data/events?${params.toString()}`);
            if (response.data.success) {
                setEvents(response.data.data || []);
                setEventsTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch events');
        } finally {
            setEventsLoading(false);
        }
    }, [canView, eventsPage, eventsRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 2) fetchEvents();
    }, [fetchEvents, canView, activeTab]);

    // ========== Tab 3: Options Flow ==========
    const fetchFlow = useCallback(async () => {
        if (!canView) return;
        setFlowLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: flowPage + 1, per_page: flowRowsPerPage });
            const response = await axiosInstance.get(`/api/sentiment-data/options-flow?${params.toString()}`);
            if (response.data.success) {
                setFlowData(response.data.data || []);
                setFlowTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch options flow');
        } finally {
            setFlowLoading(false);
        }
    }, [canView, flowPage, flowRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 3) fetchFlow();
    }, [fetchFlow, canView, activeTab]);

    // ========== Tab 4: Macro Indicators ==========
    const fetchMacro = useCallback(async () => {
        if (!canView) return;
        setMacroLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: macroPage + 1, per_page: macroRowsPerPage });
            const response = await axiosInstance.get(`/api/sentiment-data/macro-indicators?${params.toString()}`);
            if (response.data.success) {
                setMacroData(response.data.data || []);
                setMacroTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch macro indicators');
        } finally {
            setMacroLoading(false);
        }
    }, [canView, macroPage, macroRowsPerPage]);

    useEffect(() => {
        if (canView && activeTab === 4) fetchMacro();
    }, [fetchMacro, canView, activeTab]);

    // ========== Render Functions ==========
    const renderNewsSentiment = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    News Headlines & Sentiment
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel sx={{ fontFamily, fontSize: '0.8rem' }}>Symbol</InputLabel>
                        <Select
                            value={newsSymbolFilter}
                            label="Symbol"
                            onChange={(e) => { setNewsSymbolFilter(e.target.value); setNewsPage(0); }}
                            sx={{ fontFamily, fontSize: '0.8rem', borderRadius: '8px' }}
                        >
                            <MenuItem value="">
                                <em>All Symbols</em>
                            </MenuItem>
                            {watchlistSymbols.map((sym) => (
                                <MenuItem key={sym.id} value={sym.id} sx={{ fontFamily, fontSize: '0.8rem' }}>
                                    {sym.symbol} — {sym.company_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button startIcon={<RefreshIcon />} onClick={fetchNews} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                        Refresh
                    </Button>
                    {canAdd && (
                        <Button startIcon={fetchingLiveNews ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
                            variant="contained" onClick={handleFetchLiveNews} disabled={fetchingLiveNews} size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
                            {fetchingLiveNews ? 'Fetching...' : 'Fetch Latest News'}
                        </Button>
                    )}
                    {canAdd && (
                        <Button startIcon={analyzingNews ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                            variant="contained" onClick={handleAnalyzeNews} disabled={analyzingNews} size="small"
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}>
                            Analyze
                        </Button>
                    )}
                </Box>
                {liveNewsFeedback && (
                    <Alert severity={liveNewsFeedback.type} onClose={() => setLiveNewsFeedback(null)} sx={{ mt: 1, fontFamily }}>
                        {liveNewsFeedback.message}
                    </Alert>
                )}
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {newsLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Headline</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Source</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbols</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Sentiment</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Score</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Published</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }} align="center">Link</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {newsItems.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <NewspaperIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No news items found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : newsItems.map((news) => (
                                        <TableRow key={news.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Tooltip title={news.headline || news.title || ''}>
                                                    <span>{news.headline || news.title || '--'}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {news.source || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {(news.symbols || news.related_symbols || []).slice(0, 3).map((sym, i) => (
                                                        <Chip key={i} label={sym} size="small" variant="outlined"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={SentimentScoreLabels[news.sentiment] || news.sentiment || '--'} size="small"
                                                    color={SentimentScoreColors[news.sentiment] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {news.sentiment_score > 0 ? (
                                                        <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                                    ) : news.sentiment_score < 0 ? (
                                                        <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                                    ) : null}
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: news.sentiment_score > 0 ? '#10b981' : news.sentiment_score < 0 ? '#ef4444' : '#64748b', fontSize: '0.8rem' }}>
                                                        {news.sentiment_score != null ? news.sentiment_score.toFixed(3) : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {news.published_at ? new Date(news.published_at).toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {news.url && (
                                                    <IconButton size="small" href={news.url} target="_blank" rel="noopener noreferrer" sx={{ color: '#64748b' }}>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={newsTotalCount} page={newsPage}
                            onPageChange={(e, p) => setNewsPage(p)} rowsPerPage={newsRowsPerPage}
                            onRowsPerPageChange={(e) => { setNewsRowsPerPage(parseInt(e.target.value, 10)); setNewsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderSocialMedia = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Social Media Sentiment
                </Typography>
                <Button startIcon={<RefreshIcon />} onClick={fetchSocial} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Refresh
                </Button>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {socialLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Platform</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Mentions</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Sentiment</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Score</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Volume Change</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Engagement</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {socialData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No social media data found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : socialData.map((item) => (
                                        <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {item.symbol_name || item.symbol || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={item.platform || '--'} size="small" variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {item.mention_count != null ? item.mention_count.toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={SentimentScoreLabels[item.sentiment] || item.sentiment || '--'} size="small"
                                                    color={SentimentScoreColors[item.sentiment] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: item.sentiment_score > 0 ? '#10b981' : item.sentiment_score < 0 ? '#ef4444' : '#64748b', fontSize: '0.82rem' }}>
                                                {item.sentiment_score != null ? item.sentiment_score.toFixed(3) : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {item.volume_change_pct > 0 ? (
                                                        <TrendingUpIcon sx={{ fontSize: 16, color: ACCENT }} />
                                                    ) : item.volume_change_pct < 0 ? (
                                                        <TrendingDownIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                    ) : null}
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: item.volume_change_pct > 50 ? ACCENT : '#64748b', fontSize: '0.8rem' }}>
                                                        {item.volume_change_pct != null ? `${item.volume_change_pct > 0 ? '+' : ''}${item.volume_change_pct.toFixed(1)}%` : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {item.engagement_score != null ? item.engagement_score.toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {item.date ? new Date(item.date).toLocaleDateString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={socialTotalCount} page={socialPage}
                            onPageChange={(e, p) => setSocialPage(p)} rowsPerPage={socialRowsPerPage}
                            onRowsPerPageChange={(e) => { setSocialRowsPerPage(parseInt(e.target.value, 10)); setSocialPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderEvents = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Event Analysis
                </Typography>
                <Button startIcon={<RefreshIcon />} onClick={fetchEvents} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Refresh
                </Button>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {eventsLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Event</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbols</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Impact Level</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Vol Impact</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Expected Date</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {events.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No events found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : events.map((event) => (
                                        <TableRow key={event.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                <Tooltip title={event.name || event.description || ''}>
                                                    <span>{event.name || event.description || '--'}</span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {event.event_type || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {(event.affected_symbols || []).slice(0, 3).map((sym, i) => (
                                                        <Chip key={i} label={sym} size="small" variant="outlined"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
                                                    ))}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={EventImpactLevelLabels[event.impact_level] || event.impact_level || '--'} size="small"
                                                    color={EventImpactLevelColors[event.impact_level] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {event.expected_vol_impact != null ? `${event.expected_vol_impact > 0 ? '+' : ''}${(event.expected_vol_impact * 100).toFixed(1)}%` : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {event.expected_date ? new Date(event.expected_date).toLocaleDateString() : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={event.status || 'Upcoming'} size="small"
                                                    color={event.status === 'COMPLETED' ? 'default' : event.status === 'ACTIVE' ? 'success' : 'info'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={eventsTotalCount} page={eventsPage}
                            onPageChange={(e, p) => setEventsPage(p)} rowsPerPage={eventsRowsPerPage}
                            onRowsPerPageChange={(e) => { setEventsRowsPerPage(parseInt(e.target.value, 10)); setEventsPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderOptionsFlow = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Options Flow & Unusual Activity
                </Typography>
                <Button startIcon={<RefreshIcon />} onClick={fetchFlow} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Refresh
                </Button>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {flowLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Signal Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Side</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Volume</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Premium</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Vol/OI Ratio</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Expiry</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Time</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {flowData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No unusual options flow detected</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : flowData.map((flow) => (
                                        <TableRow key={flow.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {flow.symbol_name || flow.symbol || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={FlowSignalTypeLabels[flow.signal_type] || flow.signal_type || '--'} size="small"
                                                    color={FlowSignalTypeColors[flow.signal_type] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={flow.side || '--'} size="small"
                                                    color={flow.side === 'CALL' ? 'success' : flow.side === 'PUT' ? 'error' : 'default'}
                                                    variant="outlined"
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {flow.volume != null ? flow.volume.toLocaleString() : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: ACCENT, fontSize: '0.82rem' }}>
                                                {flow.premium != null ? `$${(flow.premium / 1000).toFixed(1)}K` : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(flow.vol_oi_ratio != null ? flow.vol_oi_ratio * 20 : 0, 100)}
                                                        sx={{
                                                            width: 40, height: 4, borderRadius: 2, bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': {
                                                                bgcolor: flow.vol_oi_ratio > 3 ? '#ef4444' : flow.vol_oi_ratio > 1.5 ? ACCENT : '#10b981',
                                                                borderRadius: 2,
                                                            },
                                                        }}
                                                    />
                                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                                                        {flow.vol_oi_ratio != null ? flow.vol_oi_ratio.toFixed(2) : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {flow.expiry ? new Date(flow.expiry).toLocaleDateString() : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {flow.timestamp ? new Date(flow.timestamp).toLocaleTimeString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={flowTotalCount} page={flowPage}
                            onPageChange={(e, p) => setFlowPage(p)} rowsPerPage={flowRowsPerPage}
                            onRowsPerPageChange={(e) => { setFlowRowsPerPage(parseInt(e.target.value, 10)); setFlowPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    const renderMacroIndicators = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                    Macro Indicators
                </Typography>
                <Button startIcon={<RefreshIcon />} onClick={fetchMacro} size="small" sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                    Refresh
                </Button>
            </Box>

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                {macroLoading ? (
                    <Box sx={{ p: 3 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="text" sx={{ fontSize: '1.2rem', mb: 1 }} />)}</Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Indicator</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Signal</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Current Value</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Previous</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Change</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Impact on Vol</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Last Updated</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {macroData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No macro indicator data found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : macroData.map((macro) => (
                                        <TableRow key={macro.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {macro.indicator_name || macro.indicator || '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={MacroIndicatorSignalLabels[macro.signal] || macro.signal || '--'} size="small"
                                                    color={MacroIndicatorSignalColors[macro.signal] || 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                {macro.current_value != null ? macro.current_value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {macro.previous_value != null ? macro.previous_value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '--'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {macro.change_pct > 0 ? (
                                                        <TrendingUpIcon sx={{ fontSize: 16, color: '#10b981' }} />
                                                    ) : macro.change_pct < 0 ? (
                                                        <TrendingDownIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                                    ) : null}
                                                    <Typography sx={{
                                                        fontFamily, fontWeight: 600, fontSize: '0.8rem',
                                                        color: macro.change_pct > 0 ? '#10b981' : macro.change_pct < 0 ? '#ef4444' : '#64748b',
                                                    }}>
                                                        {macro.change_pct != null ? `${macro.change_pct > 0 ? '+' : ''}${macro.change_pct.toFixed(2)}%` : '--'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                {macro.vol_impact || '--'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, color: '#94a3b8', fontSize: '0.75rem' }}>
                                                {macro.updated_at ? new Date(macro.updated_at).toLocaleString() : '--'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination component="div" count={macroTotalCount} page={macroPage}
                            onPageChange={(e, p) => setMacroPage(p)} rowsPerPage={macroRowsPerPage}
                            onRowsPerPageChange={(e) => { setMacroRowsPerPage(parseInt(e.target.value, 10)); setMacroPage(0); }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontFamily } }} />
                    </>
                )}
            </Paper>
        </Box>
    );

    // ========== Permission Guard ==========
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view Sentiment & Alternative Data.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: '16px',
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <NewspaperIcon sx={{ fontSize: 28, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                            Sentiment & Alternative Data
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                            News sentiment, social media, events, options flow, and macro indicators
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto">
                    <Tab label="News Sentiment" icon={<NewspaperIcon />} iconPosition="start" />
                    <Tab label="Social Media" icon={<ForumIcon />} iconPosition="start" />
                    <Tab label="Events" icon={<EventIcon />} iconPosition="start" />
                    <Tab label="Options Flow" icon={<WavesIcon />} iconPosition="start" />
                    <Tab label="Macro Indicators" icon={<AccountBalanceIcon />} iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && renderNewsSentiment()}
            {activeTab === 1 && renderSocialMedia()}
            {activeTab === 2 && renderEvents()}
            {activeTab === 3 && renderOptionsFlow()}
            {activeTab === 4 && renderMacroIndicators()}
        </Box>
    );
};

export default SentimentAlternativeDataPage;
